import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, Plus } from 'lucide-react';
import { api, getUploadsPublicUrl } from '../../services/api';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatDateTime } from '../../utils/date';
import { isMaintenanceRoleName } from '../../utils/investorRole';

export type ReportStatus = 'pending' | 'in_progress' | 'reviewed' | 'resolved';

function roleBadgeLabel(roleName: string | undefined): string {
  const n = (roleName ?? '').toLowerCase();
  if (n.includes('mantenimiento')) return 'Técnico';
  if (n === 'administrador' || n === 'admin') return 'Admin';
  if (n.includes('dirección') || n.includes('direccion')) return 'Dirección';
  return 'Equipo';
}

export interface MaintenanceReportQuotationSummary {
  isFree?: boolean | null;
  amount?: string | number | null;
  notes?: string | null;
  submittedAt?: string | null;
  approvedAt?: string | null;
}

export interface MaintenanceReportWorkTimelineProps {
  reportId: string;
  status: ReportStatus;
  assignedUserId?: string | null;
  readOnly?: boolean;
  /** Resumen de cotización para el inquilino o contexto del historial */
  quotation?: MaintenanceReportQuotationSummary | null;
}

function formatQuotationMoney(amount: string | number | null | undefined): string {
  if (amount === null || amount === undefined || amount === '') return '—';
  const n = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
}

function quotationBannerText(q: MaintenanceReportQuotationSummary): { title: string; detail: string } {
  if (!q.submittedAt) {
    return {
      title: 'Cotización',
      detail: 'El técnico aún no registra el costo estimado o si el servicio es sin costo.',
    };
  }
  const cost =
    q.isFree === true
      ? 'Sin costo para el inquilino'
      : `Estimado: ${formatQuotationMoney(q.amount ?? null)}`;
  if (!q.approvedAt) {
    return {
      title: 'Cotización registrada',
      detail: `${cost}. Pendiente de aprobación de administración antes de iniciar el trabajo.`,
    };
  }
  return {
    title: 'Cotización aprobada',
    detail: `${cost}. El trabajo está autorizado.`,
  };
}

export const MaintenanceReportWorkTimeline: React.FC<MaintenanceReportWorkTimelineProps> = ({
  reportId,
  status,
  assignedUserId,
  readOnly = false,
  quotation,
}) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const isAssignee = !!user?.id && assignedUserId === user.id;
  const { data: comments = [], isLoading } = useQuery({
    queryKey: ['maintenance-report-comments', reportId],
    queryFn: () => api.getMaintenanceReportComments(reportId),
    enabled: !!reportId && !readOnly,
    staleTime: 30_000,
  });

  const tenantCommentsQuery = useQuery({
    queryKey: ['tenant-maintenance-report-comments', reportId],
    queryFn: () => api.getTenantMaintenanceReportComments(reportId),
    enabled: !!reportId && readOnly,
    staleTime: 30_000,
  });

  const list = readOnly ? tenantCommentsQuery.data ?? [] : comments;
  const loading = readOnly ? tenantCommentsQuery.isLoading : isLoading;

  const addMutation = useMutation({
    mutationFn: async () => {
      let photos: string[] = [];
      if (pendingFiles.length > 0) {
        const up = await api.uploadMaintenanceReportImages(pendingFiles);
        photos = up.fileUrls ?? [];
      }
      return api.postMaintenanceReportComment(reportId, {
        content: text.trim(),
        photos,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['maintenance-report-comments', reportId] });
      await queryClient.invalidateQueries({ queryKey: ['maintenance-report', reportId] });
      await queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
      setText('');
      pendingFiles.forEach((_, i) => {
        const u = previews[i];
        if (u?.startsWith('blob:')) URL.revokeObjectURL(u);
      });
      setPendingFiles([]);
      setPreviews([]);
      setFormError(null);
    },
    onError: (err: Error) => setFormError(err.message || 'Error al enviar el comentario'),
  });

  const statusMutation = useMutation({
    mutationFn: (next: ReportStatus) => api.patchMaintenanceReportStatus(reportId, next),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['maintenance-report', reportId] });
      await queryClient.invalidateQueries({ queryKey: ['maintenance-reports'] });
    },
  });

  const canUpdateReports =
    hasPermission('update_maintenance_reports') || hasPermission('manage_maintenance_reports');
  const isMaintenanceTechnician = isMaintenanceRoleName(user?.role?.name);
  const canPost =
    !readOnly &&
    status !== 'resolved' &&
    (isMaintenanceTechnician ? isAssignee : isAssignee || canUpdateReports);
  const canMarkResolved = canPost;

  const onPickFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const next: File[] = [];
    const urls: string[] = [];
    Array.from(files).forEach((f) => {
      if (!f.type.startsWith('image/')) return;
      next.push(f);
      urls.push(URL.createObjectURL(f));
    });
    setPendingFiles((p) => [...p, ...next]);
    setPreviews((p) => [...p, ...urls]);
  };

  const removeFile = (idx: number) => {
    const u = previews[idx];
    if (u?.startsWith('blob:')) URL.revokeObjectURL(u);
    setPendingFiles((p) => p.filter((_, i) => i !== idx));
    setPreviews((p) => p.filter((_, i) => i !== idx));
  };

  const submit = () => {
    if (!text.trim() && pendingFiles.length === 0) {
      setFormError('Escribe un mensaje o adjunta al menos una foto.');
      return;
    }
    setFormError(null);
    addMutation.mutate();
  };

  const markResolved = () => {
    if (!window.confirm('¿Marcar este reporte como resuelto?')) return;
    statusMutation.mutate('resolved');
  };

  const qBanner = quotation ? quotationBannerText(quotation) : null;

  return (
    <Card title="Historial de trabajo">
      {qBanner && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm">
          <p className="font-semibold text-amber-950">{qBanner.title}</p>
          <p className="text-amber-900/90 mt-1">{qBanner.detail}</p>
          {quotation?.notes?.trim() ? (
            <p className="text-amber-900/80 mt-2 whitespace-pre-wrap border-t border-amber-200/80 pt-2">
              <span className="font-medium">Notas: </span>
              {quotation.notes.trim()}
            </p>
          ) : null}
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
        </div>
      ) : (
        <div className="space-y-4">
          {list.length === 0 ? (
            <p className="text-sm text-gray-500">Sin comentarios de trabajo aún.</p>
          ) : (
            <div className="relative pl-6 border-l-2 border-gray-200 space-y-4">
              {list.map((c: any) => (
                <div key={c.id} className="relative">
                  <div className="absolute -left-[29px] top-2 w-3 h-3 rounded-full bg-primary border-2 border-white" />
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <div className="flex flex-wrap justify-between gap-2 mb-2">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-medium text-gray-900">{c.author?.name ?? 'Usuario'}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                          {roleBadgeLabel(c.author?.roles?.name)}
                        </span>
                      </div>
                      <time className="text-xs text-gray-500">{formatDateTime(c.createdAt)}</time>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{c.content}</p>
                    {Array.isArray(c.photos) && c.photos.length > 0 && (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        {c.photos.map((url: string, i: number) => (
                          <a key={i} href={getUploadsPublicUrl(url)} target="_blank" rel="noopener noreferrer">
                            <img
                              src={getUploadsPublicUrl(url)}
                              alt=""
                              className="w-24 h-24 object-cover rounded-lg border border-gray-200 hover:opacity-90"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {readOnly && status === 'resolved' && (
            <p className="text-sm text-center text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg py-2">
              Este reporte está resuelto.
            </p>
          )}

          {canPost && (
            <div className="border-t border-gray-200 pt-4 space-y-3">
              <p className="text-sm font-medium text-gray-800">Registrar avance</p>
              {formError && (
                <div className="p-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg">{formError}</div>
              )}
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Describe el avance o trabajo realizado…"
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              {previews.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {previews.map((src, i) => (
                    <div key={i} className="relative">
                      <img src={src} alt="" className="w-20 h-20 object-cover rounded-lg border border-gray-200" />
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center hover:bg-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => onPickFiles(e.target.files)}
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <ImageIcon className="w-4 h-4" />
                    Agregar fotos
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {canMarkResolved && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={markResolved}
                      disabled={statusMutation.isPending}
                    >
                      Marcar resuelto
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    onClick={submit}
                    disabled={addMutation.isPending || (!text.trim() && pendingFiles.length === 0)}
                    className="flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    {addMutation.isPending ? 'Enviando…' : 'Enviar comentario'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
